import React from 'react';
import { Link } from 'react-router-dom';
import PdfToImage from '../components/PdfToImage';
import Seo from '../components/seo/Seo';
import { softwareAppSchema, howToSchema, breadcrumbSchema } from '../components/seo/jsonld';
import GoogleAd from '../components/GoogleAd';
import { AD_SLOTS } from '../components/adSlots';

const PATH = '/pdf-to-image';
const TITLE = 'PDF to Image Converter — Export Pages as PNG (ZIP)';
const DESCRIPTION = 'Convert every page of a PDF into a high-quality PNG image. Packaged as a ZIP. 100% in-browser, no uploads, no watermarks.';

const PdfToImagePage = () => (
    <>
        <Seo
            title={TITLE}
            description={DESCRIPTION}
            path={PATH}
            jsonLd={[
                softwareAppSchema({ name: 'PDF to Image Converter', description: DESCRIPTION, path: PATH }),
                howToSchema({
                    name: 'How to convert a PDF to PNG images',
                    description: 'Export every page of a PDF as a high-resolution PNG image, delivered as a ZIP.',
                    steps: [
                        'Pick a PDF from your device.',
                        'Click "Convert to Images (ZIP)".',
                        'Watch the progress counter — large PDFs may take a minute.',
                        'A ZIP file downloads containing one PNG per page.',
                    ],
                }),
                breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'PDF to Image', path: PATH },
                ]),
            ]}
        />

        <div className="page-container">
            <article>
                <header className="page-header">
                    <h1>PDF to Image Converter</h1>
                    <p className="page-lead">
                        Export every page of a PDF as a high-resolution PNG image, delivered as
                        a ZIP archive you can unzip on any device.
                    </p>
                </header>

                <section className="tool-embed">
                    <PdfToImage />
                </section>

                <GoogleAd slot={AD_SLOTS.PDF_TO_IMAGE} className="ad-slot-inline" />

                <section className="content-section">
                    <h2>Why convert a PDF to images?</h2>
                    <p>
                        PDFs are a compact, vector-friendly container — but there are real
                        situations where you need plain images instead:
                    </p>
                    <ul>
                        <li>
                            <strong>Social media posts.</strong> Instagram, LinkedIn carousels,
                            and WhatsApp status updates all want images, not PDFs.
                        </li>
                        <li>
                            <strong>Embedding in documents.</strong> Sometimes the cleanest way
                            to paste a page from a report into a slide deck is to export it as
                            PNG first.
                        </li>
                        <li>
                            <strong>Portfolios and showcases.</strong> A designer or illustrator
                            who produces PDF spec sheets may want raster thumbnails of each page.
                        </li>
                        <li>
                            <strong>Accessibility for apps that don&apos;t read PDFs.</strong>
                            Some legacy or mobile tooling can only display images.
                        </li>
                    </ul>

                    <h2>How the export works</h2>
                    <p>
                        Each page of your PDF is rendered to a canvas at 2&times; the native
                        display resolution (to preserve sharpness) using{' '}
                        <a href="https://mozilla.github.io/pdf.js/" target="_blank" rel="noopener noreferrer">
                            pdf.js
                        </a>
                        , Mozilla's open-source PDF renderer. The rendered canvas is then exported
                        as a PNG and bundled into a single ZIP file alongside all other pages.
                        Pages are named <code>page-1.png</code>, <code>page-2.png</code>, and so
                        on, so they sort correctly.
                    </p>

                    <h2>File size and resolution</h2>
                    <p>
                        PNG is lossless, which is the right default for documents with text and
                        sharp edges. That said, PNGs of text-heavy pages can be 1&ndash;3&nbsp;MB
                        per page — a 50-page PDF might produce a 100&nbsp;MB ZIP. If that's too
                        big for your use case, a desktop tool can export at JPG with quality
                        settings for smaller files.
                    </p>

                    <h2>Resolution and clarity</h2>
                    <p>
                        We render at 2&times; scale so the output is crisp on retina-class
                        displays. For print, you may want higher DPI — an export at 2&times; is
                        roughly 144&nbsp;DPI, which is fine for screens but not great for printed
                        brochures. We'll consider adding a resolution selector; if you need it,
                        let us know via <Link to="/contact">contact</Link>.
                    </p>

                    <h2>Step by step</h2>
                    <ol>
                        <li>Pick a PDF from your device.</li>
                        <li>Click <strong>Convert to Images (ZIP)</strong>.</li>
                        <li>Watch the progress counter. Large PDFs may take a minute.</li>
                        <li>A ZIP file downloads containing one PNG per page.</li>
                    </ol>

                    <h2>Privacy</h2>
                    <p>
                        Your PDF is read into memory by JavaScript running in your browser and is
                        rendered locally on your own CPU and GPU. Nothing is transmitted to a
                        server. If the PDF contains sensitive content, you can use this tool with
                        confidence that no trace remains beyond your own downloads folder.
                    </p>
                </section>

                <section className="content-section">
                    <h2>Related tools</h2>
                    <ul>
                        <li><Link to="/image-to-pdf">Image to PDF</Link> — reverse direction.</li>
                        <li><Link to="/compress-pdf">Compress PDF</Link> — shrink a PDF before exporting.</li>
                        <li><Link to="/pdf-to-word">PDF to Word</Link> — extract text instead of images.</li>
                    </ul>
                </section>
            </article>
        </div>
    </>
);

export default PdfToImagePage;
